// TP-Friendlies browse helpers: the date-native grouping + week-strip mapping that
// power the sticky date-group headers and the horizontal date strip. Kept pure +
// side-effect-free so the grouping rules are unit-tested directly.
//
// The backend already returns the feed soonest-first (Coalesce(date_start,
// camp_window_start)); this module NEVER re-sorts — it only labels + groups the
// order the server gives, and maps a strip bucket to a date-range filter.
import {
  parseISO,
  startOfWeek,
  endOfWeek,
  endOfMonth,
  isWithinInterval,
  format,
} from "date-fns";

// The one date a card sorts + groups on. Prefer the backend's coalesced
// `start_date`; fall back to the fixed/flexible `date.date_start` so non-camp cards
// group correctly even before that additive field lands. Camp cards without
// `start_date` return null (grouped into the calm "Upcoming" tail).
export const effectiveStartDate = (card) =>
  card?.start_date || card?.date?.date_start || null;

const toDate = (value) => {
  if (!value) return null;
  try {
    const d = value instanceof Date ? value : parseISO(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch (e) {
    return null;
  }
};

// A card's date-group: "This week" for anything up to the end of the current
// (Mon–Sun) week, else the month name ("July", and "January 2027" when the year
// differs from today's). Undated (camp missing start) → "Upcoming".
// Returns { key, label } — `key` is a sortable string so contiguous groups keep
// the server's soonest-first order.
export const dateGroup = (dateStr, today = new Date()) => {
  const d = toDate(dateStr);
  if (!d) return { key: "zzzz", label: "Upcoming" };
  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
  if (d <= weekEnd) return { key: "0000-thisweek", label: "This week" };
  const key = format(d, "yyyy-MM");
  const label =
    d.getFullYear() === today.getFullYear() ? format(d, "LLLL") : format(d, "LLLL yyyy");
  return { key, label };
};

// Group the already-sorted feed into contiguous date sections for the sticky
// headers. Each group: { key, label, sublabel, count, items }. `sublabel` is the
// min–max span of the group ("Jun 24 – 30" / "Jul 8 – 14"), matching the design's
// date-group subline.
export const groupByDate = (cards = [], today = new Date()) => {
  const groups = [];
  let current = null;
  cards.forEach((card) => {
    const ds = effectiveStartDate(card);
    const g = dateGroup(ds, today);
    if (!current || current.key !== g.key) {
      current = { key: g.key, label: g.label, items: [], _dates: [] };
      groups.push(current);
    }
    current.items.push(card);
    const d = toDate(ds);
    if (d) current._dates.push(d);
  });
  return groups.map((g) => {
    let sublabel = "";
    if (g._dates.length) {
      const min = g._dates.reduce((a, b) => (a < b ? a : b));
      const max = g._dates.reduce((a, b) => (a > b ? a : b));
      sublabel =
        min.getTime() === max.getTime()
          ? format(min, "MMM d")
          : min.getMonth() === max.getMonth()
          ? `${format(min, "MMM d")} – ${format(max, "d")}`
          : `${format(min, "MMM d")} – ${format(max, "MMM d")}`;
    }
    return { key: g.key, label: g.label, sublabel, count: g.items.length, items: g.items };
  });
};

// Map a week-strip bucket → the { date_from, date_to } filter it sets. A WEEK bucket
// (fr-be-builder 8426a23) carries its own ISO Mon–Sun range; a MONTH bucket spans the
// whole month; a legacy DAY bucket pins a single day. `null`/`all` clears.
export const bucketToRange = (bucket) => {
  if (!bucket || bucket.key === "all") return { date_from: "", date_to: "" };
  if (bucket.date_from && bucket.date_to) return { date_from: bucket.date_from, date_to: bucket.date_to };
  if (bucket.kind === "day") return { date_from: bucket.key, date_to: bucket.key };
  if (bucket.kind === "month") {
    const first = parseISO(`${bucket.key}-01`);
    return { date_from: format(first, "yyyy-MM-dd"), date_to: format(endOfMonth(first), "yyyy-MM-dd") };
  }
  return { date_from: "", date_to: "" };
};

// Which strip bucket is active for the current { date_from, date_to } — so the strip
// reflects state set by either the strip itself or the Date-range chip. Matches the
// bucket whose own range equals the filter (week/month/day uniform); no date filter →
// "all"; a custom range that matches no bucket → null (nothing highlighted).
export const activeBucketKey = (dateFrom, dateTo, buckets = []) => {
  if (!dateFrom && !dateTo) return "all";
  const hit = buckets.find((b) => {
    const r = bucketToRange(b);
    return r.date_from === dateFrom && r.date_to === dateTo;
  });
  return hit ? hit.key : null;
};

// True when this is the current (Mon–Sun) week's bucket — small helper the strip
// uses to label the leading day-run "This week" if desired.
export const isThisWeek = (dateStr, today = new Date()) => {
  const d = toDate(dateStr);
  if (!d) return false;
  return isWithinInterval(d, {
    start: startOfWeek(today, { weekStartsOn: 1 }),
    end: endOfWeek(today, { weekStartsOn: 1 }),
  });
};
